import axios from 'axios'
import React, { useState, useEffect, useReducer, useRef } from 'react'
import {
  TextField,
  Button,
  Paper,
  Grid,
  Typography,
  Link
} from '@material-ui/core'
import { useDispatch } from 'react-redux'
import DocumentTitle from 'react-document-title'
import history from 'browserHistory'
import { formReducer, getFieldError } from 'helpers/forms'
import { checkErrorStatus, setSnackbar } from 'utils/common/actions'
import { setWorkspace } from 'utils/auth/actions'
import Config from 'config'
import Loader from 'components/Loader'
import LoginBg from 'static/images/login-bg.png'
import { ReactComponent as LogoIcon } from 'static/icons/visiaw-logo-white.svg'
import IndustriesInput from 'components/IndustriesInput'
import AreasOfLawInput from 'components/AreasOfLawInput'
import useStyles from './styles'

const initFormsState = {
  errors: {}
}

const CreateWorkspacePage = () => {
  const dispatch = useDispatch()
  const { apiUrl } = Config.network
  const { termsUrl, policyUrl, contactEmail, websiteUrl } = Config.contacts
  const [isLoading, setIsLoading] = useState(false)
  const [formErrors, dispatchFormErrors] = useReducer(formReducer, initFormsState)
  const [valueIndustries, setValueIndustries] = useState([])
  const [valueAreasOfLaw, setValueAreasOfLaw] = useState([])
  const [fileName, setFileName] = useState(null)

  const localClasses = useStyles()

  const formRef = useRef()

  useEffect(() => {
    dispatchFormErrors({ type: 'reset' })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const addWorkspaceSubmit = (event) => {
    event.preventDefault()
    dispatchFormErrors({ type: 'reset' })
    setIsLoading(true)
    const url = `${apiUrl}workspaces/`
    const name = event.target.elements.name.value
    const industries = valueIndustries
    const areasOfLaw = valueAreasOfLaw
    const logos = event.target.elements.logo.files
    const formData = new FormData()
    formData.append('name', name)
    formData.append('areas_of_law', JSON.stringify(areasOfLaw))
    formData.append('industries', JSON.stringify(industries))
    if (logos && logos.length > 0) {
      formData.append('logo', logos[0])
    }
    axios.post(url, formData).then((body) => {
      const { data } = body
      console.log(data)
      dispatch(setSnackbar('Workspace created'))
      setIsLoading(false)
      dispatch(setWorkspace(data))
      history.push('/')
    }).catch(err => {
      console.log(err)
      if (err.response) {
        dispatch(checkErrorStatus(err.response.status))
        setIsLoading(false)
        if (err.response.status === 400) {
          dispatchFormErrors({
            type: 'error',
            payload: err.response.data
          })
        } else {
          dispatchFormErrors({
            type: 'error',
            payload: {
              nonField: [err.response.data.detail]
            }
          })
        }
      } else {
        dispatch(setSnackbar('Connection error', 'error'))
      }
    })
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      setFileName(file.name)
    } else {
      setFileName(null)
    }
  }

  return (
    <>
      <DocumentTitle title='Create workspace | RegulApp' />
      <Grid container>
        <Grid item md={6} xs={12}>
          <Paper className={localClasses.loginBgPaper} square elevation={0}>
            <LogoIcon className={localClasses.logoIcon} />
            <img className={localClasses.loginBgImage} src={LoginBg} alt='login-page' />
            <div className={localClasses.loginBgTermsContainer}>
              <Button href={termsUrl} target='_blank' className={localClasses.terms}>
                Terms of use
              </Button>
              <Button href={policyUrl} target='_blank' className={localClasses.private}>
                Private policy
              </Button>
            </div>
          </Paper>
        </Grid>
        <Grid item md={6} xs={12}>
          <Paper className={localClasses.loginPaper} square elevation={0}>
            {
              isLoading ? <Loader /> : null
            }
            <form onSubmit={addWorkspaceSubmit} ref={formRef} className={localClasses.innerLoginContainer}>
              <Typography className={localClasses.h2} variant='h2' color='textPrimary'>
                Setup
              </Typography>
              <Typography className={localClasses.body2} variant='body2' color='textPrimary'>
                Type your company  industry and you will be<br/>
                receiving live automated updates on the legal<br/>
                changes happening in Bulgaria and the EU in<br/>
                your industry.
              </Typography>
              { getFieldError(formErrors, 'nonField') ?
              <p className='error-message'>{getFieldError(formErrors, 'nonField')}</p> : null }

                <TextField
                  className={localClasses.textField}
                  required
                  error={!!getFieldError(formErrors, 'name')}
                  helperText={getFieldError(formErrors, 'name')}
                  label='Workspace name'
                  placeholder='E.g. RegulApp'
                  type='text'
                  color='secondary'
                  name='name'
                  fullWidth
                  variant='outlined'
                  InputLabelProps={{
                    shrink: true,
                    required: true
                  }}
                />

                <div className={`${localClasses.fileInputFormControl} ${!!getFieldError(formErrors, 'logo') && 'error'}`} >
                  <label className={localClasses.fileInputLabel} htmlFor='logo'>
                    <span className={`${localClasses.labelImitation} labelImitation`}>
                      Logo
                    </span>
                    {
                      fileName ?
                        fileName :
                        <span className={localClasses.placeholderlImitation}>
                          No file submitted
                        </span>

                    }
                  </label>
                  <input
                    accept='.png,.jpg,.jpeg'
                    onChange={handleFileChange}
                    className={localClasses.fileInput}
                    id='logo'
                    name='logo'
                    type='file'
                    tabIndex='2'
                  />
                  <p className='error-message'>
                    {
                      getFieldError(formErrors, 'logo')
                    }
                  </p>
                </div>

                <AreasOfLawInput
                  error={!!getFieldError(formErrors, 'areas_of_law')}
                  helperText={getFieldError(formErrors, 'areas_of_law')}
                  setValue={setValueAreasOfLaw}
                  className={localClasses.textField}
                  InputLabelProps={{
                    shrink: true,
                    required: true
                  }}
                />

                <IndustriesInput
                  error={!!getFieldError(formErrors, 'industries')}
                  helperText={getFieldError(formErrors, 'industries')}
                  setValue={setValueIndustries}
                  className={localClasses.textField}
                  InputLabelProps={{
                    shrink: true,
                    required: true
                  }}
                />

                <div className={localClasses.actions}>
                  <Button
                    onClick={() => formRef.current.dispatchEvent(new Event('submit'))}
                    className={localClasses.actionButton}
                    color='primary'
                    variant='contained'
                    // type='submit'
                  >
                    Let's start!
                  </Button>
                </div>
                <Typography variant='body2' color='textPrimary' className={localClasses.contactUs}>
                  <Link href={`mailto:${contactEmail}`} target='_blank' underline='always'>Contact us</Link> if you have any questions.
                </Typography>
                <Typography variant='body2' color='textSecondary' align='center'  className={localClasses.learnMore}>
                  Want to learn more about the RegulApp,<br/>follow <Link color='inherit' href={websiteUrl} target='_blank' underline='always'>this page</Link>
                </Typography>
            </form>
          </Paper>
        </Grid>
      </Grid>
    </>
  )
}

export default CreateWorkspacePage
